import { fetchAuthSession } from "aws-amplify/auth"; // You are using AWS Amplify for authentication
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Amplify } from "aws-amplify";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import awsExports from "./aws-exports"; // Import the AWS configuration

import LoadingIndicator from "./components/LoadingIndicator";
import { LoadingProvider } from "./context/LoadingContext";

import { Toaster } from "sonner";
import { useShallow } from "zustand/react/shallow";
import AdminGate from "./admin/AdminGate";
import AdminPanel from "./admin/AdminPanel";
import DrawerButton from "./components/DrawerButton";
import { isAdminHost } from "./config/adminHost";
import Content from "./layout/Content";
import Sidebar from "./layout/Sidebar";
import AdverseEvents from "./pages/AdverseEvents";
import ForgotPassword from "./pages/ForgotPassword";
import Login from "./pages/Login";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Register from "./pages/Register";
import ServiceAgreement from "./pages/ServiceAgreement";
import { useAppStore } from "./store/useAppStore";

// Configure AWS Amplify
Amplify.configure(awsExports);

const ProtectedRoute = ({ children }) => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(null);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const session = await fetchAuthSession(); // Check if the user is authenticated
        if (session) {
          setIsAuthenticated(true);
        }
      } catch {
        setIsAuthenticated(false); // If not authenticated, redirect to login
        navigate("/login");
      }
    };
    checkAuth();
  }, [navigate]);

  if (isAuthenticated === null) return <LoadingIndicator />; // Show loading indicator while checking authentication

  return isAuthenticated ? children : null; // Only render children if authenticated
};

const App = () => {
  const [selectedTab, setSelectedTab] = useState("PredicateFinder"); // Default selected tab
  const sidebarVisible = useAppStore(
    useShallow((state) => state.sidebarVisible),
  );

  return (
    <LoadingProvider>
      <LoadingIndicator />
      <Toaster richColors closeButton position="top-right" />
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/register" element={<Register />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                {isAdminHost() ? (
                  <AdminGate>
                    <AdminPanel />
                  </AdminGate>
                ) : (
                  <div className="h-screen overflow-x-auto">
                    {/* CSS drawer system for small screens */}
                    <div className="drawer lg:hidden">
                      <input
                        id="left-sidebar-drawer"
                        type="checkbox"
                        className="drawer-toggle"
                      />
                      <div className="drawer-content">
                        <DrawerButton />
                        <Content selectedTab={selectedTab} />
                      </div>
                      <div className="drawer-side">
                        <label
                          htmlFor="left-sidebar-drawer"
                          className="drawer-overlay"
                        ></label>
                        <Sidebar
                          selectedTab={selectedTab}
                          setSelectedTab={setSelectedTab}
                        />
                      </div>
                    </div>

                    {/* React state system for large screens */}
                    <div className="hidden min-w-full lg:flex">
                      {sidebarVisible && (
                        <div className="w-60 flex-shrink-0">
                          <Sidebar
                            selectedTab={selectedTab}
                            setSelectedTab={setSelectedTab}
                          />
                        </div>
                      )}
                      <div className="flex min-w-0 flex-1 flex-col">
                        <DrawerButton />
                        <Content selectedTab={selectedTab} />
                      </div>
                    </div>
                  </div>
                )}
              </ProtectedRoute>
            }
          />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/service-agreement" element={<ServiceAgreement />} />
          <Route path="/adverse-events" element={<ProtectedRoute><AdverseEvents /></ProtectedRoute>} />
        </Routes>
      </Router>
    </LoadingProvider>
  );
};

export default App;
